import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { Taxonomy } from 'app/components/editor/sidebar/tags/taxonomy/Taxonomy';
import { buildUsedConceptRoles } from 'app/components/editor/sidebar/tags/taxonomy/usedConceptRoles';
import { useTagsByDocument } from 'entities/tag';

export interface AnchorPickerResult {
  /** Concept QName, e.g. "ifrs-full:PropertyPlantAndEquipment". Resolved to a
   * UUID via useConcept at submit time. */
  conceptName: string;
}

type AnchorPickerProps = {
  /** Wider anchor must resolve to a standard concept (ESEF rule — no
   * chain-of-extensions). Narrower anchors may reference either. */
  anchorKind: 'wider' | 'narrower';
  documentSuperId: number;
  onSelect: (picked: AnchorPickerResult) => void;
  onClose: () => void;
  selected?: string | null;
};

export const AnchorPicker = ({ anchorKind, documentSuperId, onSelect, onClose, selected }: AnchorPickerProps) => {
  const { t } = useTranslation('common');

  const [localSelected, setLocalSelected] = useState<string | null>(selected || null);
  const hideExtensions = anchorKind === 'wider';
  const { data: tagsData } = useTagsByDocument(documentSuperId, { include: ['concept', 'metadata', 'unit', 'context'] });
  const usedConceptRoles = useMemo(() => buildUsedConceptRoles(tagsData?.tags ?? []), [tagsData]);

  return (
    <div className="new-extension__taxonomy">
      <Taxonomy
        selectedTagId={localSelected}
        isContentMarked={false}
        usedConceptRoles={usedConceptRoles}
        documentTags={[]}
        hideExtensions={hideExtensions}
        hideFooter
        onSelectionChange={(tag) => setLocalSelected(tag?.id ?? null)}
      />

      <div className="new-extension__footer-actions">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          {t('cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!localSelected}
          onClick={() => {
            if (localSelected) {
              onSelect({ conceptName: localSelected });
              onClose();
            }
          }}
        >
          {t('apply')}
        </button>
      </div>
    </div>
  );
};
